import { Close, Edit } from '@tunasong/icons'
import { IconButton } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './scaffold-rail.module.css'

export interface ScaffoldRailProps {
  /** The sketch's entity name, shown after the status word. */
  name?: string
  /** Viewers see the label but never the controls. */
  readOnly?: boolean
  onDiscard: () => void
}

/**
 * The status rail under a sketch: what this block is, and the one move that resolves it.
 *
 * Only Discard is offered here. Replace lands with the flow that backs it. Keep lives in the
 * block menu on purpose — promotion is a decision, not a reflex, and putting it a click away
 * from Discard invites accidentally promoting exactly the material a musician is most
 * ambivalent about.
 *
 * The visible word is "Sketch", never "AI" (see docs/ai-language.md). Provenance is not hidden,
 * it simply is not the headline: it lives in the Properties popover and in the media's
 * `aria-describedby`, where someone asking "where did this come from?" gets a straight answer.
 */
export const ScaffoldRail: FC<ScaffoldRailProps> = props => {
  const { name, readOnly = false, onDiscard } = props

  return (
    <div role="group" aria-label="Sketch controls" className={styles.rail} contentEditable={false}>
      <span className={styles.label}>
        <Edit size={12} aria-hidden={true} />
        <span>Sketch</span>
        {name ? (
          <>
            <span aria-hidden={true}>·</span>
            <span className={styles.name}>{name}</span>
          </>
        ) : null}
      </span>
      {readOnly ? null : (
        <span className={styles.actions}>
          <IconButton className={styles.action} size="small" aria-label="Discard sketch" onClick={onDiscard}>
            <Close size={14} />
          </IconButton>
        </span>
      )}
    </div>
  )
}

export default ScaffoldRail
